import React from 'react';
import _ from 'lodash';
import { useCallback, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IRootDispatch, IRootState } from 'stores/rematch/root-store';
import { createContext, useContextSelector } from 'use-context-selector';
import { BookingView } from 'stores/rematch/models/types/bookings-store';
import { useBookingsQuery } from './bookings-query-context';

const noop = () => {};

type BookingViewTabContextType = {
  dirtyIds: string[];
  revertViewSnapshot: (customId: string) => void;
  revertSnapshot: () => void;
  updateViewSnapshot: (view: BookingView) => void;
  updateSnapshot: (opts?: { skipUpdateDefaultViews?: boolean }) => void;
  removeViewFromSnapshot: (customId: string) => void;
};

const BookingTabViewContext = createContext<BookingViewTabContextType>({
  dirtyIds: [],
  revertViewSnapshot: noop,
  revertSnapshot: noop,
  updateViewSnapshot: noop,
  updateSnapshot: noop,
  removeViewFromSnapshot: noop,
});

interface BookingTabViewProviderProps {
  children: React.ReactNode;
}

export const BookingTabViewProvider = ({ children }: BookingTabViewProviderProps) => {
  const { bookingsStore } = useDispatch<IRootDispatch>();
  const defaultBookingViews = useSelector((state: IRootState) => state.bookingsStore.defaultBookingViews);
  const displayedBookingListingTabs = useSelector(
    (state: IRootState) => state.bookingsStore.displayedBookingListingTabs,
  );
  const setBookingsFilterV2 = bookingsStore.setBookingsFilterV2;
  const defaultViewIds = useMemo(() => defaultBookingViews.map((view) => view.customViewId), [defaultBookingViews]);
  const snapshot = useRef(_.cloneDeep([...defaultBookingViews, ...displayedBookingListingTabs]));
  const fetchBookings = useBookingsQuery((state) => state.fetchBookings);
  const setHasBookingListingFilterChanged = bookingsStore.setHasBookingListingFilterChanged;

  const revertViewSnapshot = useCallback(
    async (customId: string) => {
      const tabToRevert = snapshot.current.find((tab) => tab.customViewId === customId);
      if (tabToRevert) {
        setHasBookingListingFilterChanged(true);
        setBookingsFilterV2(tabToRevert.filterValue);
        await fetchBookings({
          filters: tabToRevert.filterValue,
          caller: 'BookingsViewTabs.revertViewSnapshot',
          reset: true,
          viewId: tabToRevert?.customViewId,
        });
      }
    },
    [setBookingsFilterV2, fetchBookings, setHasBookingListingFilterChanged],
  );

  const revertSnapshot = useCallback(() => {
    // biome-ignore lint/complexity/noForEach: yay for forEach
    snapshot.current.forEach((tab) => {
      setBookingsFilterV2(tab.filterValue);
    });
  }, [setBookingsFilterV2]);

  const updateViewSnapshot = useCallback((next: BookingView) => {
    const customViewId = next.customViewId;
    const currentTab = snapshot.current.find((tab) => tab.customViewId === customViewId);

    if (!currentTab) {
      snapshot.current.push(_.cloneDeep(next));
      return;
    }
    snapshot.current = snapshot.current.map((tab) => {
      if (tab.customViewId === customViewId) {
        return _.cloneDeep(next);
      }
      return tab;
    });
  }, []);

  const removeViewFromSnapshot = useCallback((customId: string) => {
    snapshot.current = snapshot.current.filter((tab) => tab.customViewId !== customId);
  }, []);

  const updateSnapshot = useCallback(
    (opts?: {
      skipUpdateDefaultViews?: boolean;
    }) => {
      if (opts?.skipUpdateDefaultViews) {
        const defaultTabsPrev = snapshot.current.filter((tab) => defaultViewIds.includes(tab.customViewId));
        snapshot.current = _.cloneDeep([...defaultTabsPrev, ...displayedBookingListingTabs]);
        return;
      }
      snapshot.current = _.cloneDeep([...defaultBookingViews, ...displayedBookingListingTabs]);
    },
    [defaultBookingViews, displayedBookingListingTabs, defaultViewIds],
  );

  const dirtyIds = useMemo(() => {
    const dirtyIds: string[] = [];
    const currentTabs = [...defaultBookingViews, ...displayedBookingListingTabs];
    // biome-ignore lint/complexity/noForEach: yay for forEach
    snapshot.current.forEach((tab) => {
      const currentTab = currentTabs.find((t) => t.customViewId === tab.customViewId);
      if (!_.isEqual(currentTab?.filterValue, tab.filterValue)) {
        if (currentTab?.customViewId) {
          dirtyIds.push(currentTab.customViewId);
        }
      }
    });
    return dirtyIds;
  }, [defaultBookingViews, displayedBookingListingTabs]);

  const value = useMemo(
    () => ({
      dirtyIds,
      revertViewSnapshot,
      revertSnapshot,
      updateViewSnapshot,
      updateSnapshot,
      removeViewFromSnapshot,
    }),
    [dirtyIds, revertViewSnapshot, revertSnapshot, updateViewSnapshot, updateSnapshot, removeViewFromSnapshot],
  );

  return <BookingTabViewContext.Provider value={value}>{children}</BookingTabViewContext.Provider>;
};

type Selector<T, Context> = (value: Context) => T;

export const useBookingTabs = <T,>(selector: Selector<T, BookingViewTabContextType>) =>
  useContextSelector(BookingTabViewContext, selector);
